import type { MasterResult } from "@/lib/master-engine";

type Severity = "low" | "medium" | "high" | "critical";
type Effort = "low" | "medium" | "high";
type Impact = "low" | "medium" | "high";
type Owner =
  | "CEO / Founder"
  | "Head of Sales"
  | "Sales Manager"
  | "RevOps"
  | "Marketing"
  | "Account Executive"
  | "SDR / BDR"
  | "Team";

export interface RevenueLeakItem {
  key: string;
  title: string;
  stage: string;
  currentValue: number;
  benchmarkValue: number;
  gapValue: number;
  monthlyRevenueLeak: number;
  annualRevenueLeak: number;
  severity: Severity;
  explanation: string;
  rootCause: string;
}

export interface ActionTask {
  title: string;
  description: string;
  owner: Owner;
  dueInDays: number;
  effort: Effort;
  impact: Impact;
  successMetric: string;
  steps: string[];
}

export interface PriorityPlan {
  rank: number;
  key: string;
  title: string;
  whyNow: string;
  problem: string;
  rootCause: string;
  businessImpact: string;
  revenueLeakMonthly: number;
  revenueLeakAnnual: number;
  severity: Severity;
  owner: Owner;
  tasks: ActionTask[];
  kpis: string[];
  expectedShortTermImpact: string;
  expected30DaySignal: string;
}

export interface QuickWinPlan {
  title: string;
  description: string;
  whyItWorks: string;
  owner: Owner;
  dueInDays: number;
  successMetric: string;
  implementation: string;
  expectedImpact: string;
}

export interface ThirtyDayPlanWeek {
  week: string;
  goal: string;
  focus: string;
  actions: string[];
}

export interface NinetyDayPlan {
  phase_1_0_30: {
    title: string;
    goal: string;
    focus: string;
    actions: string[];
  };
  phase_2_30_60: {
    title: string;
    goal: string;
    focus: string;
    actions: string[];
  };
  phase_3_60_90: {
    title: string;
    goal: string;
    focus: string;
    actions: string[];
  };
}

export interface Phase2Hook {
  title: string;
  text: string;
  trigger: string;
}

export interface ActionPlanEngineOutput {
  topIssues: RevenueLeakItem[];
  revenueLeakMap: RevenueLeakItem[];
  quickWins: QuickWinPlan[];
  priorityPlans: PriorityPlan[];
  plan30Days: ThirtyDayPlanWeek[];
  plan90Days: NinetyDayPlan;
  phase2Hooks: Phase2Hook[];
  aiPayload: Record<string, unknown>;
}

function n(v: unknown, fallback = 0): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function euro(value: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n(value));
}

function clampSeverity(score: number): Severity {
  if (score >= 85) return "critical";
  if (score >= 60) return "high";
  if (score >= 35) return "medium";
  return "low";
}

function stageGapSeverity(current: number, benchmark: number): Severity {
  const gap = Math.max(0, benchmark - current);
  if (gap >= 25) return "critical";
  if (gap >= 15) return "high";
  if (gap >= 8) return "medium";
  return "low";
}

function ownerForKey(key: string): Owner {
  if (key.includes("pipeline")) return "Head of Sales";
  if (key.includes("follow")) return "Sales Manager";
  if (key.includes("crm")) return "RevOps";
  if (key.includes("pace")) return "Head of Sales";
  if (key.includes("discovery")) return "Sales Manager";
  if (key.includes("presentation")) return "Account Executive";
  if (key.includes("positioning")) return "CEO / Founder";
  return "Head of Sales";
}

function makeTask(
  title: string,
  description: string,
  owner: Owner,
  dueInDays: number,
  effort: Effort,
  impact: Impact,
  successMetric: string,
  steps: string[]
): ActionTask {
  return { title, description, owner, dueInDays, effort, impact, successMetric, steps };
}

function getTopLeverLeak(result: MasterResult, rank: number) {
  const lever = Array.isArray(result.topLevers) ? result.topLevers[rank] : undefined;
  return {
    title: (lever as { title?: string })?.title || ("Hebel " + (rank + 1)),
    monthlyImpact: n((lever as { monthlyImpact?: unknown })?.monthlyImpact),
    annualImpact: n((lever as { annualImpact?: unknown })?.annualImpact),
    whyItMatters: (lever as { whyItMatters?: string })?.whyItMatters || "",
  };
}

function buildFunnelLeaks(result: MasterResult): RevenueLeakItem[] {
  const metrics = result.computedMetrics;

  const leak1: RevenueLeakItem = {
    key: "contact_to_discovery",
    title: "Zu wenige Leads werden zu qualifizierten Erstgesprächen",
    stage: "Kontakt → Discovery",
    currentValue: n(metrics?.convContactToDiscovery),
    benchmarkValue: n(metrics?.benchmarkContactToDiscovery, 30),
    gapValue: Math.max(0, n(metrics?.benchmarkContactToDiscovery, 30) - n(metrics?.convContactToDiscovery)),
    monthlyRevenueLeak: Math.round(getTopLeverLeak(result, 0).monthlyImpact || n(metrics?.monthlyRevenueGap) * 0.4),
    annualRevenueLeak: Math.round(getTopLeverLeak(result, 0).annualImpact || n(metrics?.annualRevenueGap) * 0.4),
    severity: stageGapSeverity(n(metrics?.convContactToDiscovery), n(metrics?.benchmarkContactToDiscovery, 30)),
    explanation:
      "Im oberen Funnel geht zu viel Potenzial verloren. Interesse wird nicht schnell genug in qualifizierte Gespräche überführt.",
    rootCause:
      "Typische Ursachen sind unklare Lead-Ownership, schwache Vorqualifizierung, langsame Reaktionszeiten und fehlende Follow-up-Standards.",
  };

  const leak2: RevenueLeakItem = {
    key: "discovery_to_presentation",
    title: "Discovery erzeugt zu wenig echte Kaufreife",
    stage: "Discovery → Präsentation / Angebot",
    currentValue: n(metrics?.convDiscoveryToPresentation),
    benchmarkValue: n(metrics?.benchmarkDiscoveryToPresentation, 65),
    gapValue: Math.max(0, n(metrics?.benchmarkDiscoveryToPresentation, 65) - n(metrics?.convDiscoveryToPresentation)),
    monthlyRevenueLeak: Math.round(getTopLeverLeak(result, 1).monthlyImpact || n(metrics?.monthlyRevenueGap) * 0.35),
    annualRevenueLeak: Math.round(getTopLeverLeak(result, 1).annualImpact || n(metrics?.annualRevenueGap) * 0.35),
    severity: stageGapSeverity(n(metrics?.convDiscoveryToPresentation), n(metrics?.benchmarkDiscoveryToPresentation, 65)),
    explanation:
      "Gespräche erzeugen nicht konsequent genug Klarheit über Problem, Priorität, Budget, Buying-Center und nächsten Schritt.",
    rootCause:
      "Discovery ist zu feature-lastig, Entscheider werden zu spät eingebunden und Opportunities werden zu früh in spätere Phasen geschoben.",
  };

  const leak3: RevenueLeakItem = {
    key: "presentation_to_deal",
    title: "Zu viele Angebote bleiben ohne Abschluss",
    stage: "Präsentation / Angebot → Deal",
    currentValue: n(metrics?.convPresentationToDeal),
    benchmarkValue: n(metrics?.benchmarkPresentationToDeal, 35),
    gapValue: Math.max(0, n(metrics?.benchmarkPresentationToDeal, 35) - n(metrics?.convPresentationToDeal)),
    monthlyRevenueLeak: Math.round(getTopLeverLeak(result, 2).monthlyImpact || n(metrics?.monthlyRevenueGap) * 0.25),
    annualRevenueLeak: Math.round(getTopLeverLeak(result, 2).annualImpact || n(metrics?.annualRevenueGap) * 0.25),
    severity: stageGapSeverity(n(metrics?.convPresentationToDeal), n(metrics?.benchmarkPresentationToDeal, 35)),
    explanation:
      "Späte Funnel-Phasen konvertieren unter Potenzial. Das kostet direkt Umsatz, weil bereits investierte Vertriebszeit nicht monetarisiert wird.",
    rootCause:
      "Fehlende Multi-Threading-Strategie, unklare Value-Story, schwache Verhandlungsvorbereitung und keine harte Next-Step-Disziplin.",
  };

  return [leak1, leak2, leak3].sort((a, b) => b.monthlyRevenueLeak - a.monthlyRevenueLeak);
}

function buildRepPerformanceLeaks(result: MasterResult): RevenueLeakItem[] {
  const team = result.teamPerformance;
  if (!team?.summarySignals) return [];

  const leaks: RevenueLeakItem[] = [];

  if (team.summarySignals.hasPacingProblem) {
    leaks.push({
      key: "pace_gap",
      title: "Team liegt unter Soll-Pace",
      stage: "Teamsteuerung / Zielerreichung",
      currentValue: n(team.pacingVsPlanPct),
      benchmarkValue: 100,
      gapValue: Math.max(0, 100 - n(team.pacingVsPlanPct)),
      monthlyRevenueLeak: Math.round(n(team.pacingRecoveryPotentialMonthly) || n(team.projectedYearEndGap) / 12),
      annualRevenueLeak: Math.round(n(team.projectedYearEndGap)),
      severity: clampSeverity(Math.max(0, 100 - n(team.pacingVsPlanPct))),
      explanation:
        "Das Team produziert aktuell unter dem benötigten Sollverlauf für das Jahresziel.",
      rootCause:
        "Nicht der Markt ist das Hauptproblem, sondern fehlende Zielsteuerung, unklare Weekly Reviews und zu wenig operative Konsequenz pro Rep.",
    });
  }

  if (team.summarySignals.hasTargetBreakdownProblem) {
    leaks.push({
      key: "target_breakdown",
      title: "Ziele sind nicht sauber auf Reps heruntergebrochen",
      stage: "Zielsystem / Verantwortlichkeit",
      currentValue: n(team.targetManagementScore),
      benchmarkValue: 80,
      gapValue: Math.max(0, 80 - n(team.targetManagementScore)),
      monthlyRevenueLeak: Math.round(n(team.repAlignmentPotentialMonthly)),
      annualRevenueLeak: Math.round(n(team.repAlignmentPotentialMonthly) * 12),
      severity: clampSeverity(Math.max(0, 80 - n(team.targetManagementScore))),
      explanation:
        "Fehlende Zieltransparenz reduziert Verantwortlichkeit, Forecast-Qualität und Execution.",
      rootCause:
        "Jahres- und Quartalsziele sind operativ nicht sauber in individuelle Zielbeiträge, Rep-Steuerung und Review-Routinen übersetzt.",
    });
  }

  if (team.summarySignals.hasPipelineProblem) {
    leaks.push({
      key: "pipeline_coverage",
      title: "Pipeline-Abdeckung pro Rep ist zu schwach",
      stage: "Pipeline / Zielerreichung",
      currentValue: n(team.pipelineReadinessScore),
      benchmarkValue: 80,
      gapValue: Math.max(0, 80 - n(team.pipelineReadinessScore)),
      monthlyRevenueLeak: Math.round(n(team.pipelineActivationPotentialMonthly)),
      annualRevenueLeak: Math.round(n(team.pipelineActivationPotentialMonthly) * 12),
      severity: clampSeverity(Math.max(0, 80 - n(team.pipelineReadinessScore))),
      explanation:
        "Ein Teil des Teams hat nicht genug belastbare Pipeline, um sein Ziel realistisch zu erreichen.",
      rootCause:
        "Zu wenig qualifizierter Input, schlechte Deal-Hygiene, fehlende Priorisierung und kein aktives Risikomanagement je Opportunity.",
    });
  }

  return leaks;
}

// Website-Leak entfernt — ausschließlich Sales-Process-Metriken relevant
function buildWebsiteLeak(_result: MasterResult): RevenueLeakItem[] {
  return [];
}

function buildRevenueLeakMap(result: MasterResult): RevenueLeakItem[] {
  return [
    ...buildRepPerformanceLeaks(result),
    ...buildFunnelLeaks(result),
    ...buildWebsiteLeak(result),
  ]
    .filter((x) => x.monthlyRevenueLeak > 0)
    .sort((a, b) => b.monthlyRevenueLeak - a.monthlyRevenueLeak)
    .slice(0, 6);
}

function buildTasksForIssue(issue: RevenueLeakItem): ActionTask[] {
  const owner = ownerForKey(issue.key);

  switch (issue.key) {
    case "pace_gap":
      return [
        makeTask(
          "Weekly Pace Review einführen",
          "Jeder Rep bekommt ein festes Weekly Review mit Soll-Ist-Abgleich, Gap und Recovery-Plan.",
          owner, 3, "low", "high",
          "Jeder Rep kennt Soll-Umsatz, Ist-Umsatz und die individuelle Ziellücke.",
          [
            "Monatsziel je Rep aus Jahresziel ableiten",
            "Aktuellen Pace-Stand je Rep sichtbar machen",
            "Für jeden Rep eine Recovery-Maßnahme definieren",
            "Review jede Woche gleicher Tag, gleiche Struktur",
          ]
        ),
        makeTask(
          "Rep-Zielblatt verbindlich machen",
          "Jeder Rep erhält ein Zielblatt mit Monatsziel, Pipeline-Soll und Activity-Soll.",
          owner, 5, "low", "high",
          "100% der Reps haben ein aktives Zielblatt und Weekly Commitments.",
          [
            "Ziel pro Rep berechnen",
            "Erforderliche Deals, Präsentationen und Discovery Calls ergänzen",
            "Commitment pro Rep für die kommende Woche festhalten",
          ]
        ),
        makeTask(
          "Top-Performer-Verhalten standardisieren",
          "Unterschiede zwischen Top-Performern und Durchschnitt messbar machen und als Team-Standard definieren.",
          "Sales Manager", 10, "medium", "high",
          "Mindestens 3 Top-Performer-Praktiken sind als Standard eingeführt.",
          [
            "Top 2 Performer interviewen",
            "Gemeinsame Muster in Discovery, Follow-up und Deal-Progression extrahieren",
            "Diese Muster ins Team-Playbook überführen",
          ]
        ),
      ];

    case "target_breakdown":
      return [
        makeTask(
          "Jahresziel in Monats- und Rep-Ziele übersetzen",
          "Zielsystem operativ machen statt nur auf Jahresniveau zu kommunizieren.",
          owner, 2, "low", "high",
          "Für jeden Rep gibt es ein Monatsziel mit Soll-Stand.",
          [
            "Gesamtziel in Quartals- und Monatsziele zerlegen",
            "Monatsziel je Rep auf Basis Rolle und Kapazität ableiten",
            "Zieltransparenz im CRM oder Dashboard sichtbar machen",
          ]
        ),
        makeTask(
          "Forecast-Kategorien vereinheitlichen",
          "Pipeline darf nur nach einheitlichen Kriterien in Forecast-Kategorien laufen.",
          "RevOps", 7, "medium", "high",
          "Forecast-Review basiert auf klaren Eintrittskriterien je Phase.",
          [
            "Definitionen für Commit / Best Case / Pipeline festlegen",
            "Eintrittskriterien je Kategorie schriftlich definieren",
            "Bestehende Pipeline anhand der Regeln bereinigen",
          ]
        ),
      ];

    case "pipeline_coverage":
      return [
        makeTask(
          "Pipeline-Coverage pro Rep berechnen",
          "Nicht nur Team-Pipeline betrachten, sondern belastbare Coverage pro Rep sichtbar machen.",
          owner, 3, "low", "high",
          "Jeder Rep hat eine Coverage-Zahl und Risikoeinschätzung.",
          [
            "Ziel pro Rep vs. offene belastbare Pipeline gegenüberstellen",
            "Deals ohne klares Next Step oder Entscheiderkontakt markieren",
            "Risikodeals separat ausweisen",
          ]
        ),
        makeTask(
          "Pipeline-Review auf Deal-Risiken umstellen",
          "Keine Status-Meetings mehr. Nur Risiko, Blocker, Nächster Schritt.",
          "Sales Manager", 5, "low", "high",
          "Jeder reviewed Deal hat klaren Owner, Blocker und Deadline.",
          [
            "Review-Agenda neu definieren",
            "Pro Deal nur 3 Fragen: Warum gewinnen wir? Was blockiert? Was ist der nächste terminierte Schritt?",
            "Deals ohne belastbaren Progress eskalieren oder schließen",
          ]
        ),
        makeTask(
          "Stalled Deals reaktivieren oder bereinigen",
          "Inaktive Opportunities verzerren Forecast und binden Zeit.",
          "Account Executive", 7, "low", "medium",
          "Alle Deals >14 Tage ohne Fortschritt sind bearbeitet.",
          [
            "Deals >14 Tage ohne Aktivität filtern",
            "Reaktivierungs-Sequenz starten",
            "Ohne Bewegung: Closed Lost oder Nurture",
          ]
        ),
      ];

    case "contact_to_discovery":
      return [
        makeTask(
          "Lead-Response-SLA einführen",
          "Neue Leads müssen innerhalb klarer Zeitfenster bearbeitet werden.",
          "SDR / BDR", 2, "low", "high",
          "90% aller qualifizierten Leads werden innerhalb der SLA kontaktiert.",
          [
            "SLA definieren, z. B. <2 Stunden werktags",
            "Owner je Lead-Typ festlegen",
            "SLA-Monitoring im CRM oder Sheet bauen",
          ]
        ),
        makeTask(
          "Follow-up-Standard auf 5 Touchpoints erhöhen",
          "Ein Kontaktversuch reicht nicht. Es braucht einen festen Multi-Touch-Standard.",
          "Sales Manager", 4, "low", "high",
          "Ø Follow-ups pro Lead steigen auf mindestens 5.",
          [
            "5-Touch-Sequenz für 10 Werktage definieren",
            "Kanäle kombinieren: Mail, Telefon, LinkedIn",
            "Keine Leads ohne Next Step offen lassen",
          ]
        ),
        makeTask(
          "Lead-Qualifizierung vor Discovery schärfen",
          "Unpassende Leads sollen früher erkannt werden, gute Leads schneller zum Termin kommen.",
          "Head of Sales", 7, "medium", "high",
          "Show-Rate und Discovery-Rate steigen innerhalb 30 Tagen messbar.",
          [
            "K.O.-Kriterien definieren",
            "Lead Intake Fragen standardisieren",
            "Routing-Regeln für hochwertige Leads priorisieren",
          ]
        ),
      ];

    case "discovery_to_presentation":
      return [
        makeTask(
          "Discovery-Framework verbindlich machen",
          "Jeder Discovery Call folgt derselben Struktur: Problem, Priorität, Business Impact, Entscheidungsprozess, Timing.",
          owner, 4, "medium", "high",
          "100% der Discovery Calls nutzen das gleiche Framework.",
          [
            "Pflichtfragen definieren",
            "Call-Template erstellen",
            "Call-Notizen im CRM verpflichtend machen",
          ]
        ),
        makeTask(
          "Kein Angebot ohne Kaufreife-Check",
          "Angebote gehen nur raus, wenn Pain, Nutzen, Buying-Center und Next Step klar sind.",
          "Sales Manager", 5, "low", "high",
          "Angebote ohne vollständige Qualifizierung sinken auf nahezu null.",
          [
            "Exit-Kriterien für Discovery definieren",
            "Checkliste im CRM hinterlegen",
            "Manager-Freigabe bei Grenzfällen einführen",
          ]
        ),
        makeTask(
          "Entscheider früher einbinden",
          "Ohne wirtschaftlichen Entscheider wird aus Aktivität selten Umsatz.",
          "Account Executive", 10, "medium", "high",
          "Quote der Opportunities mit Entscheiderkontakt steigt deutlich.",
          [
            "Frage nach Buying-Center im Erstgespräch verpflichtend machen",
            "Multi-Threading ab Phase 2 einführen",
            "Opportunities ohne Entscheiderkontakt markieren",
          ]
        ),
      ];

    case "presentation_to_deal":
      return [
        makeTask(
          "Angebote mit klarer Entscheidungsführung bauen",
          "Angebote dürfen nicht nur Informationen liefern, sondern die Kaufentscheidung führen.",
          owner, 5, "medium", "high",
          "Angebotsquote zu Abschluss steigt innerhalb 30–45 Tagen.",
          [
            "Angebotsstruktur vereinheitlichen",
            "Business Case / ROI sichtbar machen",
            "Jedes Angebot mit klarem Entscheidungsfahrplan versehen",
          ]
        ),
        makeTask(
          "Verhandlungs- und Einwand-Playbook einführen",
          "Standardisierte Antworten auf die häufigsten Einwände erhöhen Abschlusswahrscheinlichkeit.",
          "Sales Manager", 7, "low", "medium",
          "Team nutzt ein gemeinsames Playbook für die Top 10 Einwände.",
          [
            "Top 10 Einwände sammeln",
            "Antwortlogiken und Gegenfragen formulieren",
            "Im Team 2 Rollenspiele pro Woche durchführen",
          ]
        ),
        makeTask(
          "Next-Step-Disziplin härten",
          "Jeder späte Deal braucht einen terminierten nächsten Schritt statt offenem Schwebezustand.",
          "Account Executive", 3, "low", "high",
          "90% der Spätphasen-Deals haben einen terminierten Next Step.",
          [
            "Jeden Deal auf terminierte nächste Aktion prüfen",
            "Offene Angebote ohne Termin eskalieren",
            "No next step = no forecast commit",
          ]
        ),
      ];

    case "website_conversion":
      // Fallthrough auf default — kein Website-Case mehr aktiv
      return [
        makeTask(
          "Inbound-Qualifizierungsprozess definieren",
          "Eingehende Anfragen brauchen einen klaren, schnellen Qualifizierungspfad.",
          "Head of Sales", 3, "low", "high",
          "Jede Inbound-Anfrage wird innerhalb definierter SLA qualifiziert.",
          [
            "Qualifizierungsfragen für eingehende Anfragen standardisieren",
            "SLA für Erstreaktion festlegen (max. 2 Stunden werktags)",
            "Routing-Regeln je Lead-Quelle definieren",
          ]
        ),
        makeTask(
          "Inbound-to-Discovery-Rate steigern",
          "Inbound-Kontakte konvertieren zu selten in terminierte Discovery Calls.",
          "SDR / BDR", 5, "low", "high",
          "Konversionsrate Inbound zu Discovery Call steigt messbar.",
          [
            "Follow-up-Sequenz auf 5 Touchpoints innerhalb 10 Tagen ausbauen",
            "Personalisierte Erstansprache mit konkretem Mehrwert formulieren",
            "Automatische Erinnerungen für offene Inbound-Leads einrichten",
          ]
        ),
      ];

    default:
      return [
        makeTask(
          "Kritischen Engpass operationalisieren",
          "Der stärkste Umsatzhebel wird in einen festen 14-Tage-Plan überführt.",
          owner, 7, "medium", "high",
          "Klar definierter KPI-Verbesserungsplan ist aktiv.",
          [
            "Engpass definieren",
            "Owner festlegen",
            "KPI und Review-Rhythmus verankern",
          ]
        ),
      ];
  }
}

function buildPriorityPlans(result: MasterResult, revenueLeaks: RevenueLeakItem[]): PriorityPlan[] {
  return revenueLeaks.slice(0, 3).map((issue, index) => {
    const tasks = buildTasksForIssue(issue);
    const kpiByKey: Record<string, string> = {
      contact_to_discovery: "Kontakt-zu-Discovery-Rate",
      discovery_to_presentation: "Discovery-zu-Präsentation-Rate",
      presentation_to_deal: "Angebot-zu-Abschluss-Rate",
      pace_gap: "Pacing vs. Plan",
      pipeline_coverage: "Pipeline-Coverage pro Rep",
    };
    const signalByKey: Record<string, string> = {
      contact_to_discovery: "Mehr qualifizierte Erstgespräche bei gleichem Leadvolumen.",
      discovery_to_presentation: "Weniger unqualifizierte Opportunities und klarere Deal-Qualität.",
      presentation_to_deal: "Mehr Abschlussđộng in bestehenden Angeboten.",
      pace_gap: "Mehr operative Transparenz und geringerer Zielrückstand.",
    };

    return {
      rank: index + 1,
      key: issue.key,
      title: issue.title,
      whyNow:
        issue.severity === "critical"
          ? "Weil hier aktuell der größte direkte Umsatzverlust entsteht und jede Verzögerung die Lücke vergrößert."
          : "Weil dieser Engpass kurzfristig Einfluss auf Conversion, Forecast und Umsatz hat.",
      problem: issue.explanation,
      rootCause: issue.rootCause,
      businessImpact:
        "Aktuell gehen hier geschätzt " + euro(issue.monthlyRevenueLeak) + " pro Monat bzw. " +
        euro(issue.annualRevenueLeak) + " pro Jahr verloren.",
      revenueLeakMonthly: issue.monthlyRevenueLeak,
      revenueLeakAnnual: issue.annualRevenueLeak,
      severity: issue.severity,
      owner: ownerForKey(issue.key),
      tasks,
      kpis: [
        kpiByKey[issue.key] || "Umsetzungs-KPI",
        "Anzahl terminierter Next Steps",
        "Wöchentlicher Fortschritt vs. Ziel",
      ],
      expectedShortTermImpact:
        issue.monthlyRevenueLeak > 15000
          ? "Hoher kurzfristiger Hebel mit direktem Einfluss auf qualifizierte Gespräche, Pipeline-Qualität oder Abschlüsse."
          : "Spürbares Signal innerhalb von 2–6 Wochen, wenn die Maßnahmen konsequent umgesetzt werden.",
      expected30DaySignal:
        signalByKey[issue.key] ||
        "Mehr Klarheit, geringere Leckage und bessere Steuerbarkeit.",
    };
  });
}

function buildQuickWins(priorityPlans: PriorityPlan[]): QuickWinPlan[] {
  const wins: QuickWinPlan[] = [];

  for (const plan of priorityPlans) {
    const firstTask = plan.tasks[0];
    if (!firstTask) continue;

    wins.push({
      title: firstTask.title,
      description: firstTask.description,
      whyItWorks:
        'Dieser Schritt adressiert direkt den Engpass "' + plan.title + '" und erzeugt früh ein sichtbares Signal im Funnel.',
      owner: firstTask.owner,
      dueInDays: Math.min(firstTask.dueInDays, 14),
      successMetric: firstTask.successMetric,
      implementation: firstTask.steps.join(" → "),
      expectedImpact:
        plan.revenueLeakMonthly > 10000
          ? "Wenn sauber umgesetzt, kann dieser Schritt einen Teil von " + euro(plan.revenueLeakMonthly) + " monatlichem Verlust zurückholen."
          : "Erhöht kurzfristig Transparenz, Verbindlichkeit und Conversion-Chance.",
    });

    if (wins.length === 3) break;
  }

  return wins.slice(0, 3);
}

function build30DayPlan(priorityPlans: PriorityPlan[]): ThirtyDayPlanWeek[] {
  const plan1 = priorityPlans[0];
  const plan2 = priorityPlans[1];
  const plan3 = priorityPlans[2];

  return [
    {
      week: "Woche 1",
      goal: "Transparenz herstellen und Owner festlegen",
      focus: "Top-Engpass sichtbar machen und in operative Verantwortung überführen",
      actions: [
        plan1?.tasks[0]?.title || "Top-Engpass definieren",
        plan1?.tasks[1]?.title || "Verantwortliche benennen",
        "Alle relevanten KPIs in einem Weekly Review zusammenziehen",
      ],
    },
    {
      week: "Woche 2",
      goal: "Standards einführen",
      focus: "Discovery, Follow-up, Pipeline oder Zielsteuerung standardisieren",
      actions: [
        plan1?.tasks[1]?.title || "Standardprozess einführen",
        plan2?.tasks[0]?.title || "Zweiten Engpass operationalisieren",
        "Exit-Kriterien und Mindeststandards verbindlich machen",
      ],
    },
    {
      week: "Woche 3",
      goal: "Qualität und Verbindlichkeit erhöhen",
      focus: "Opportunities härter qualifizieren und Next Steps terminieren",
      actions: [
        plan1?.tasks[2]?.title || "Engpass vertiefen",
        plan2?.tasks[1]?.title || "Deal-Hygiene schärfen",
        "Alle aktiven Deals auf Risiko und nächsten festen Schritt prüfen",
      ],
    },
    {
      week: "Woche 4",
      goal: "Erstes Signal messen und nachschärfen",
      focus: "Was wirkt, was blockiert, wo muss Management eingreifen?",
      actions: [
        "KPI-Signal gegen Ausgangswert vergleichen",
        plan3?.tasks[0]?.title || "Dritten Hebel anstoßen",
        "Die wirksamsten Maßnahmen in einen 60-Tage-Standard überführen",
      ],
    },
  ];
}

function build90DayPlan(priorityPlans: PriorityPlan[]): NinetyDayPlan {
  const p1 = priorityPlans[0];
  const p2 = priorityPlans[1];
  const p3 = priorityPlans[2];

  return {
    phase_1_0_30: {
      title: "0–30 Tage: Stabilisieren",
      goal: "Den größten Umsatzverlust sichtbar machen und operativ unter Kontrolle bringen",
      focus: "Ownership, Review-Rhythmus, klare Standards",
      actions: [
        p1?.tasks[0]?.title || "Top-Engpass operationalisieren",
        p1?.tasks[1]?.title || "Standard definieren",
        p2?.tasks[0]?.title || "Zweiten Engpass anstoßen",
      ],
    },
    phase_2_30_60: {
      title: "30–60 Tage: Systematisieren",
      goal: "Was funktioniert, wird verbindlicher Standard im Team",
      focus: "Replizierbare Prozesse, KPI-Steuerung, Playbooks",
      actions: [
        p1?.tasks[2]?.title || "Playbook ableiten",
        p2?.tasks[1]?.title || "Qualität und Kontrolle erhöhen",
        p3?.tasks[0]?.title || "Dritten Hebel absichern",
      ],
    },
    phase_3_60_90: {
      title: "60–90 Tage: Skalieren",
      goal: "Verbesserungen dauerhaft in Forecast, Funnel und Team-Execution verankern",
      focus: "Management-Routine, Coaching, saubere Pipeline- und Conversion-Steuerung",
      actions: [
        "Teamweite Review- und Coaching-Struktur fest verankern",
        "KPI-Ziele je Funnel-Stufe verbindlich machen",
        "Die größten Wirkhebel in Phase 2 strukturiert ausrollen",
      ],
    },
  };
}

function buildPhase2Hooks(priorityPlans: PriorityPlan[]): Phase2Hook[] {
  return [
    {
      title: "Execution schlägt Erkenntnis",
      text:
        "Die Diagnose zeigt klar, wo Umsatz verloren geht. Der entscheidende Hebel liegt jetzt nicht mehr im Verstehen, sondern in der sauberen Umsetzung mit Ownership, Review-Rhythmus und Standards.",
      trigger: priorityPlans[0]?.title || "Top-Engpass",
    },
    {
      title: "Einzelmaßnahmen reichen nicht",
      text:
        "Die stärksten Hebel hängen zusammen: Zielsteuerung, Funnel-Standards, Discovery-Qualität und Deal-Führung wirken nur dann nachhaltig, wenn sie als System gebaut werden.",
      trigger: priorityPlans[1]?.title || "Systemischer Hebel",
    },
    {
      title: "Phase 2 ist kein Zusatz, sondern Multiplikator",
      text:
        "Sobald die Prioritäten klar sind, entsteht der größte Unterschied durch konsequente Implementierung, Training und Management-Kadenz über mehrere Wochen.",
      trigger: priorityPlans[2]?.title || "Implementierung",
    },
  ];
}

export function buildActionPlanAIContext(
  result: MasterResult,
  output: ActionPlanEngineOutput
): Record<string, unknown> {
  const metrics = result.computedMetrics;
  const team = result.teamPerformance || null;
  const goalPath = metrics?.goalPath || null;

  return {
    company: {
      name: result.normalizedData?.companyName || (result as Record<string, unknown>).companyName || "Ihr Unternehmen",
      industry: result.normalizedData?.industry || "B2B",
      team_size: result.normalizedData?.teamSize || null,
    },
    revenue_context: {
      current_revenue: n(metrics?.currentRevenue),
      potential_revenue: n(metrics?.potentialRevenue),
      monthly_gap:
        n(team?.projectedYearEndGap) > 0
          ? Math.round(n(team?.projectedYearEndGap) / 12)
          : n(metrics?.monthlyRevenueGap),
      annual_gap:
        n(team?.projectedYearEndGap) > 0
          ? n(team?.projectedYearEndGap)
          : n(metrics?.annualRevenueGap),
    },
    team_context: team
      ? {
          productive_reps: n(team.productiveReps),
          pacing_vs_plan_pct: n(team.pacingVsPlanPct),
          target_management_score: n(team.targetManagementScore),
          pipeline_readiness_score: n(team.pipelineReadinessScore),
          rep_health_score: n(team.repHealthScore),
          summary_signals: team.summarySignals || null,
        }
      : null,
    funnel_context: {
      contact_to_discovery_current: n(metrics?.convContactToDiscovery),
      contact_to_discovery_benchmark: n(metrics?.benchmarkContactToDiscovery, 30),
      discovery_to_presentation_current: n(metrics?.convDiscoveryToPresentation),
      discovery_to_presentation_benchmark: n(metrics?.benchmarkDiscoveryToPresentation, 65),
      presentation_to_deal_current: n(metrics?.convPresentationToDeal),
      presentation_to_deal_benchmark: n(metrics?.benchmarkPresentationToDeal, 35),
    },
    goal_path: goalPath
      ? {
          required_contacts_per_month_from_now: n((goalPath as Record<string, unknown>).requiredContactsPerMonthFromNow),
          required_discovery_calls_per_month_from_now: n((goalPath as Record<string, unknown>).requiredDiscoveryCallsPerMonthFromNow),
          required_presentations_per_month_from_now: n((goalPath as Record<string, unknown>).requiredPresentationsPerMonthFromNow),
          required_deals_per_month_from_now: n((goalPath as Record<string, unknown>).requiredDealsPerMonthFromNow),
          required_monthly_revenue_from_now: n((goalPath as Record<string, unknown>).requiredMonthlyRevenueFromNow),
        }
      : null,
    top_issues: output.topIssues.map((i, idx) => ({
      rank: idx + 1,
      key: i.key,
      title: i.title,
      stage: i.stage,
      current: i.currentValue,
      benchmark: i.benchmarkValue,
      gap: i.gapValue,
      monthly_leak: i.monthlyRevenueLeak,
      annual_leak: i.annualRevenueLeak,
      severity: i.severity,
      explanation: i.explanation,
      root_cause: i.rootCause,
    })),
    quick_wins: output.quickWins,
    priority_plans: output.priorityPlans.map((p) => ({
      rank: p.rank,
      title: p.title,
      why_now: p.whyNow,
      revenue_leak_monthly: p.revenueLeakMonthly,
      revenue_leak_annual: p.revenueLeakAnnual,
      owner: p.owner,
      tasks: p.tasks.map((t) => ({
        title: t.title,
        owner: t.owner,
        due_in_days: t.dueInDays,
        effort: t.effort,
        impact: t.impact,
        success_metric: t.successMetric,
        steps: t.steps,
      })),
      expected_30_day_signal: p.expected30DaySignal,
    })),
    output_rules: {
      use_numbers_not_fluff: true,
      be_specific_and_operator_level: true,
      make_priority_1_dominant: true,
      show_loss_then_action_then_signal: true,
      every_quick_win_must_include_owner_deadline_metric: true,
    },
  };
}

export function buildActionPlanEngine(result: MasterResult): ActionPlanEngineOutput {
  const revenueLeakMap = buildRevenueLeakMap(result);
  const topIssues = revenueLeakMap.slice(0, 3);
  const priorityPlans = buildPriorityPlans(result, topIssues);
  const quickWins = buildQuickWins(priorityPlans);
  const plan30Days = build30DayPlan(priorityPlans);
  const plan90Days = build90DayPlan(priorityPlans);
  const phase2Hooks = buildPhase2Hooks(priorityPlans);

  const baseOutput: ActionPlanEngineOutput = {
    topIssues,
    revenueLeakMap,
    quickWins,
    priorityPlans,
    plan30Days,
    plan90Days,
    phase2Hooks,
    aiPayload: {},
  };

  return {
    ...baseOutput,
    aiPayload: buildActionPlanAIContext(result, baseOutput),
  };
}
